import './applyVehicleSelectionPage.scss';

import KeyboardBackspaceRoundedIcon from '@mui/icons-material/KeyboardBackspaceRounded';
import MenuRoundedIcon from '@mui/icons-material/MenuRounded';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import LinearProgress from '@mui/material/LinearProgress';
import { ThemeProvider } from '@mui/material/styles';
import isEmpty from 'lodash/isEmpty';
import { ReactNode, useEffect, useState } from 'react';
import { FieldValues, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import VehicleImagePlaceholder from '../../../assets/vehicle-placeholder.png';
import { CircularProgressFullScreen } from '../../../components/CircularProgressFullScreen/CircularProgressFullScreen';
import { NavigationPane } from '../../../components/NavigationPane/NavigationPane';
import { RadioCardGroup } from '../../../components/RadioCardGroup/RadioCardGroup';
import { RadioOptionClickEvent } from '../../../components/RadioGroup/types/RadioOptionClickEvent';
import { isValidClickEvent } from '../../../components/utils/isValidClickEvent';
import useAnalyticStore from '../../../store/analyticStore/useAnalyticStore';
import { HydrateSource } from '../../../store/applyStore/enums/HydrateSource';
import { getVehicleSelectionId } from '../../../store/applyStore/functions/getVehicleSelectionId';
import useApplyStore from '../../../store/applyStore/useApplyStore';
import { getVehicleDefaultImage } from '../../../store/dashboardStore/functions/getVehicleDefaultImage';
import useNavigationPaneStore from '../../../store/navigationPaneStore/useNavigationPaneStore';
import useThemeStore from '../../../store/themeStore/useThemeStore';
import useWorkflowStore from '../../../store/workflowStore/useWorkflowStore';
import { locator } from '../../../testing/e2e-locator/locator';

export function Component(): ReactNode {
  const { createTheme } = useThemeStore();
  const theme = createTheme();
  const navigate = useNavigate();

  const { t } = useTranslation();
  const { next, previous } = useWorkflowStore();
  const { setDrawerOpen } = useNavigationPaneStore();
  const {
    sendVehicleSelectionFoundAnalytics,
    sendVehicleSelectionMissingAnalytics,
  } = useAnalyticStore();

  const [isInitialSelection, setIsInitialSelection] = useState(false);

  const {
    computed: { hasVehiclesForSelection },
    isFetchingVehicleSelections,
    isHydrating,
    vehicleSelectionId,
    vehicleSelections,
    hydrate,
    setManualVehicleSelection,
    setVehicleSelectionId,
  } = useApplyStore();

  const { handleSubmit, register, watch } = useForm({
    defaultValues: {
      vehicleSelectionIdValue: vehicleSelectionId,
    },
  });

  const navigateEnterManually = (): void => {
    setManualVehicleSelection();
    navigate(next());
  };

  const previousStep = (): void => {
    navigate(previous());
  };

  const enterManuallyClicked = (): void => {
    sendVehicleSelectionMissingAnalytics();
    navigateEnterManually();
  };

  const radioOptionClicked = (event: RadioOptionClickEvent<string>): void => {
    if (!('target' in event && 'value' in event.target)) {
      return;
    }

    setIsInitialSelection(
      isEmpty(vehicleSelectionId) && isValidClickEvent(event),
    );
  };

  const formSubmit = async (values: FieldValues): Promise<void> => {
    const { vehicleSelectionIdValue } = values;

    if (!vehicleSelectionIdValue) {
      return;
    }

    setVehicleSelectionId(vehicleSelectionIdValue);

    sendVehicleSelectionFoundAnalytics();

    await hydrate({
      vehicleSelectionId: vehicleSelectionIdValue,
      source: HydrateSource.VEHICLE_SELECTION,
    });

    navigate(next());
  };

  useEffect((): void => {
    if (isInitialSelection) {
      handleSubmit(formSubmit)();
    }
  }, [isInitialSelection]);

  useEffect((): void => {
    if (!hasVehiclesForSelection) {
      navigateEnterManually();
    }
  }, [hasVehiclesForSelection]);

  if (isFetchingVehicleSelections) {
    return <CircularProgressFullScreen />;
  }

  return (
    <div className="apply-vehicle-selection-page">
      <ThemeProvider theme={theme}>
        <Box sx={theme.pageLayout}>
          <header data-testid={locator.pages.applyVehicleSelection.header.root}>
            <button
              aria-label="Previous step"
              data-testid={
                locator.pages.applyVehicleSelection.header.previousStepButton
              }
              type="button"
              onClick={previousStep}
            >
              <KeyboardBackspaceRoundedIcon />
            </button>
            <button
              aria-label="Toggle drawer"
              data-testid={
                locator.pages.applyVehicleSelection.header.drawerButton
              }
              type="button"
              onClick={(): void => {
                setDrawerOpen(true);
              }}
            >
              <MenuRoundedIcon />
            </button>
          </header>

          <div className="progress-container" role="complementary">
            <LinearProgress
              aria-label="Application completed percentage"
              data-testid={locator.pages.applyVehicleSelection.progress.root}
              value={useWorkflowStore.getState().computed.progress}
              variant="determinate"
            />
          </div>

          <main>
            <form
              data-testid={locator.pages.applyVehicleSelection.form.root}
              noValidate
              onSubmit={handleSubmit(formSubmit)}
            >
              <div className="form-content">
                <h1 data-testid={locator.pages.applyVehicleSelection.form.h1}>
                  {t('ApplyVehicleSelectionPage.title')}
                </h1>
                <h2 data-testid={locator.pages.applyVehicleSelection.form.h2}>
                  {t('ApplyVehicleSelectionPage.subtitle')}
                </h2>

                <div style={{ marginBottom: '3rem' }}>
                  <RadioCardGroup
                    name="vehicleSelectionIdValue"
                    options={vehicleSelections.map((vehicle, index) => ({
                      title: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
                      subtitle: `${vehicle.trim}`,
                      image:
                        getVehicleDefaultImage(vehicle) ||
                        VehicleImagePlaceholder,
                      value: getVehicleSelectionId(vehicle),
                      testId:
                        locator.pages.applyVehicleSelection.form.getRadioOption(
                          index,
                        ),
                    }))}
                    register={register}
                    testId={
                      locator.pages.applyVehicleSelection.form.radioGroupRoot
                    }
                    watch={watch}
                    onClick={radioOptionClicked}
                  />
                </div>

                <div style={{ marginBottom: '3rem' }}>
                  <Button
                    color="primary"
                    data-testid={
                      locator.pages.applyVehicleSelection.form.submitButton
                    }
                    disabled={isEmpty(vehicleSelectionId)}
                    loading={isHydrating}
                    startIcon={<></>}
                    type="submit"
                    variant="contained"
                    fullWidth
                  >
                    {t('ApplyVehicleSelectionPage.continue')}
                  </Button>

                  <p className="form-enter-manually">
                    <button
                      data-testid={
                        locator.pages.applyVehicleSelection.form
                          .enterManuallyButton
                      }
                      type="button"
                      onClick={enterManuallyClicked}
                    >
                      {t('ApplyVehicleSelectionPage.enterManually')}
                    </button>
                  </p>
                </div>
              </div>
              <footer
                data-testid={locator.pages.applyVehicleSelection.footer.root}
              >
                {t('copyright', { year: new Date().getFullYear() })}
              </footer>
            </form>
          </main>
        </Box>

        <NavigationPane withDrawer withUserNameHeader />
      </ThemeProvider>
    </div>
  );
}

Component.displayName = 'ApplyVehicleSelectionPage';
